import React, { useState, useEffect, useContext } from 'react'

import { useNavigate } from 'react-router-dom'
import { FEATURE_TABBED_SERVICE_SETUP_ENABLED } from '../env'

// Grommet
import { ThemeContext } from 'grommet'
import { normalizeColor } from 'grommet/utils'

import LocalStorageService from '../services/LocalStorageService'
import OrganisationTagService from '../services/OrganisationTagService'
import OrganisationService from '../services/OrganisationService'
import NotificationService from '../services/NotificationService'
import NotificationRuleService from '../services/NotificationRuleService'
import TermsService, { recordTerms } from '../services/TermsService'

// Appt Components
import Anchor from '../components/simple/anchor/Anchor'
import Box from '../components/simple/box/Box'
import CheckBox from '../components/simple/input/CheckBox'
import Divider from '../components/simple/divider/Divider'
import FontAwesome from '../components/compound/fontAwesome/FontAwesome'
import Form from '../components/simple/form/Form.js'
import Text from '../components/simple/text/Text'
import TextInput from '../components/simple/input/TextInput'
import ServiceSetupTabs from './shared/ServiceSetupTabs'

import SelectDynamic from 'react-select';
import CreatableSelect from 'react-select/creatable'


// Shared Components
import BreadcrumbBar from './shared/BreadcrumbBar'
import {
  ServiceDetailsContent,
  TechniquesContent,
  ClickersContent,
  PermissionsTabContent,
  RecipientsContent,
  NotificationRulesContent,
  PILTabContent,
  AIContent,
  WondeContent,
  CPOMSContent
} from './shared/ServiceSetupSections'

import constants from '../utils/constants'
import { beSmartTheme } from '../utils/beSmartTheme'
import ActionButton from '../components/compound/actionButton/ActionButton.js'
import UserService from '../services/UserService.js'
import { isAnyAdmin } from '../utils/permissions'
import PulseLoader from 'react-spinners/PulseLoader.js'
import UserGroupService from '../services/UserGroupService'

let ruleRowCounter = 0
const nextRowKey = () => `rule-${ruleRowCounter++}`

const compareText = (a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' })

// Ungrouped clickers sort after grouped ones
const compareClickers = (a, b) => {
  if (Boolean(a.group) !== Boolean(b.group)) return a.group ? -1 : 1
  return compareText(a.group, b.group) || compareText(a.quickBehaviour, b.quickBehaviour)
}

function RuleSelect({ options, value, onChange, labelKey, valueKey, multiple, placeholder, savedOptions = [], creatable = false, formatOptionLabel }) {
  const theme = useContext(ThemeContext) || beSmartTheme
  const primaryColor = normalizeColor(theme.global.colors.primary ? 'primary' : 'brand', theme)
  const toOption = option => ({
    value: valueKey ? option[valueKey.key] : option,
    label: labelKey ? option[labelKey] : option
  })
  const selectOptions = options.map(toOption)
  const availableOptions = [...selectOptions, ...savedOptions.map(toOption)]
  const singleValues = value == null ? [] : [value]
  const selectedValues = multiple ? (value || []) : singleValues
  const selectedOptions = selectedValues.map(selectedValue => {
    const option = availableOptions.find(item => String(item.value) === String(selectedValue))
    return { value: selectedValue, label: option?.label || String(selectedValue) }
  })

  const SelectComponent = creatable ? CreatableSelect : SelectDynamic
  const creatableProps = creatable ? { formatCreateLabel: input => `Add "${input}"` } : {}

  return (
    <SelectComponent
      {...creatableProps}
      formatOptionLabel={formatOptionLabel}
      aria-label={placeholder}
      isMulti={!!multiple}
      isClearable
      closeMenuOnSelect={!multiple}
      placeholder={placeholder}
      options={selectOptions}
      value={multiple ? selectedOptions : selectedOptions[0] || null}
      menuPortalTarget={document.body}
      theme={selectTheme => ({
        ...selectTheme,
        colors: {
          ...selectTheme.colors,
          primary: primaryColor,
          primary25: `color-mix(in srgb, ${primaryColor} 15%, white)`,
          primary50: `color-mix(in srgb, ${primaryColor} 30%, white)`,
          primary75: `color-mix(in srgb, ${primaryColor} 60%, white)`
        }
      })}
      styles={{
        container: base => ({ ...base, minWidth: 180, width: '100%' }),
        control: (base, state) => ({
          ...base,
          borderColor: 'transparent',
          boxShadow: 'none',
          backgroundColor: state.isFocused ? '#f5f5f5' : base.backgroundColor,
          '&:hover': { borderColor: 'transparent' }
        }),
        multiValueLabel: base => ({ ...base, whiteSpace: 'normal', overflowWrap: 'anywhere' }),
        menuPortal: base => ({ ...base, zIndex: 9999, fontFamily: theme.global.font.family })
      }}
      onChange={selection => onChange(multiple
        ? (selection || []).map(option => option.value)
        : selection?.value ?? null)}
    />
  )
}

function ServiceSetup() {
  const navigate = useNavigate()
  const tabbedSetupEnabled = FEATURE_TABBED_SERVICE_SETUP_ENABLED === 'true'
  const [activeTabIndex, setActiveTabIndex] = useState(() => {
    const tab = new URLSearchParams(window.location.search).get('tab')
    return /^[0-4]$/.test(tab) ? Number(tab) : 0
  })

  const [behaviourLeads, setBehaviourLead] = useState([])
  const [safeguardingLeads, setSafeguardingLeads] = useState([])
  const [otherSystemAdmins, setOtherSystemAdmins] = useState([])
  const [originalOtherSystemAdmins, setOriginalOtherSystemAdmins] = useState([])
  const [otherSystemAdminsHasChanged, setOtherSystemAdminsHasChanged] = useState(false)

  const [deescalationTechniques, setDeescalationTechnique] = useState([])
  const [deescalationAddButton, setDeescalationAddButton] = useState(false)

  const [physicalTechniques, setPhysicalTechnique] = useState([])
  const [physicalAddButton, setPhysicalAddButton] = useState(false)

  const [quickBehaviours, setQuickBehaviours] = useState([])
  const [groupColours, setGroupColours] = useState({})

  // Has the Service got terms ?
  const [serviceHasTerms, setServiceHasTerms] = useState(false)
  const [wondeIntegration, setWondeIntegration] = useState(false)

  const [cpomsEnabled, setCpomsEnabled] = useState(false)
  const [cpomsSyncEverything, setCpomsSyncEverything] = useState(false)
  const [cpomsDomain, setCpomsDomain] = useState(null)

  const [enablePILExtras, setEnablePILExtras] = useState(false);
  const [aiTermsAccepted, setAiTermsAccepted] = useState(false);

  const [allStaff, setAllStaff] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isAuthorised, setIsAuthorised] = useState(false);

  const allAcademicYears = constants.academicYears
  const apiToken = LocalStorageService.get('apiToken')
  const activeOrg = LocalStorageService.get('activeOrg')

  // Initialize userDetails otherwise we get error
  // A component is changing an uncontrolled input of type text to be controlled
  const [serviceDetails, setServiceDetails] = useState({
    serviceUserTerm: '',
    serviceUserTermPlural: '',
    serviceTerm: '',
    serviceTermPlural: '',
    ...(tabbedSetupEnabled ? Object.fromEntries(recordTerms.map(({ key }) => [key, ''])) : {}),
    academicYearStartDay: '1',
    academicYearStartMonth: 'September',
    enabledAcademicYears: [],
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    safeguardingFirstName: '',
    safeguardingLastName: '',
    safeguardingPhone: '',
    safeguardingEmail: '',
    deescalationTechnique: '',
    physicalTechnique: ''
  })

  const [notificationRules, setNotificationRules] = useState([])
  const [originalRuleUserIds, setOriginalRuleUserIds] = useState([])
  const [allStudents, setAllStudents] = useState([])
  const [allStudentGroups, setAllStudentGroups] = useState([])

  const getNotificationRules = async () => {
    const rules = await NotificationRuleService.getRules()

    // rowKey is client-only and must be stripped before saving
    const rulesArray = Object.keys(rules || {}).map(key => ({ ...rules[key], rowKey: nextRowKey() }))
    setNotificationRules(rulesArray)
    setOriginalRuleUserIds(rulesArray.map(rule => rule.user))
  }
  useEffect(() => {
    if (tabbedSetupEnabled) void getNotificationRules()
  }, [])

  const updateRule = (rowKey, field, value) => {
    setNotificationRules(rules => rules.map(rule => (rule.rowKey === rowKey ? { ...rule, [field]: value } : rule)))
  }

  const addRuleRow = () => {
    setNotificationRules(rules => [...rules, {
      rowKey: nextRowKey(),
      user: null,
      organisation: activeOrg.id,
      yearGroup: [],
      studentGroup: [],
      student: []
    }])
  }

  const removeRuleRow = (rowKey) => {
    setNotificationRules(rules => rules.filter(rule => rule.rowKey !== rowKey))
  }

  // Remove a technique from the list
  const removeTechnique = (type, index) => {
    if (type === 'deescalation') {
      const newDeescalationTechniques = deescalationTechniques.slice(0, index).concat(deescalationTechniques.slice(index + 1, deescalationTechniques.length))
      // const newDeescalationTechniques = deescalationTechniques
      // newDeescalationTechniques.splice(index, 1)
      setDeescalationTechnique(newDeescalationTechniques)
    } else {
      const newPhysicalTechniques = physicalTechniques.slice(0, index).concat(physicalTechniques.slice(index + 1, physicalTechniques.length))
      setPhysicalTechnique(newPhysicalTechniques)
    }
  }

  const saveNotificationRules = async () => {
    const currentRules = notificationRules.filter(rule => rule.user != null)
    const rulesToSubmit = currentRules.map(rule => ({
      userId: rule.user,
      forYearGroups: rule.yearGroup,
      forStudentGroups: rule.studentGroup.map(group => group.id),
      forStudents: rule.student.map(student => student.id)
    }))
    originalRuleUserIds
      .filter(userId => !currentRules.some(rule => String(rule.user) === String(userId)))
      .forEach(userId => rulesToSubmit.push({ userId, forYearGroups: [], forStudentGroups: [], forStudents: [] }))

    const result = await NotificationRuleService.updateRules(rulesToSubmit)
    if (!result || result.error) {
      NotificationService.error('Your notification rules could not be saved')
      return false
    }
    return true
  }

  // Submit form values
  // ToDo: Add error checking and loading spinner
  const submitForm = async (values) => {
    const tagsToSubmit = []

    const clickersToSubmit = tabbedSetupEnabled ? quickBehaviours
      .map(item => ({
        quickBehaviour: (item.quickBehaviour || '').trim(),
        selected: !!item.selected,
        group: item.group || null,
        colour: (item.group && groupColours[item.group]) || null
      }))
      .filter(item => item.quickBehaviour !== '') : quickBehaviours
    const clickerNames = tabbedSetupEnabled ? clickersToSubmit.map(item => item.quickBehaviour.toLowerCase()) : []
    if (tabbedSetupEnabled && new Set(clickerNames).size !== clickerNames.length) {
      NotificationService.error('Quick behaviour already exists, please add a different one')
      return
    }

    // Set up tags for Service Defaults
    const serviceDefaultsToSubmit = ['serviceUserTerm', 'serviceUserTermPlural', 'serviceTerm', 'serviceTermPlural', 'academicYearStartDay', 'academicYearStartMonth', 'enabledAcademicYears', ...(tabbedSetupEnabled ? recordTerms.map(({ key }) => key) : [])]

    constants.pilKeys.forEach(pilKey => {
      let pilTermValue = values[TermsService.getPILTermKeyToTagKey(pilKey.key)]
      tagsToSubmit.push({
        organisation: activeOrg.id,
        key: TermsService.getPILTermKeyToTagKey(pilKey.key),
        type: 'besmart',
        value: pilTermValue
      })
    })

    // If field being saved is a term tag AND service has no terms
    // then set to null to clear value
    const termTags = ['academicYearStartDay', 'academicYearStartMonth', 'enabledAcademicYears']

    serviceDefaultsToSubmit.forEach(field => {

      let termValue = null
      if (!serviceHasTerms && termTags.includes(field)) {
        termValue = null
      } else {
        if (field === 'enabledAcademicYears') {
          termValue = JSON.stringify(values[field])
        } else if (recordTerms.some(({ key }) => key === field)) {
          termValue = (values[field] || '').trim()
        } else {
          termValue = values[field]
        }
      }

      tagsToSubmit.push({
        organisation: activeOrg.id,
        key: field,
        type: 'besmart',
        // value: field === 'enabledAcademicYears' ? JSON.stringify(values[field]) : values[field]
        value: termValue
      })
    })

    const optionalLists = [
      ['behaviourLeads', behaviourLeads],
      ['safeguardingLeads', safeguardingLeads],
      ['deescalationTechniques', deescalationTechniques],
      ['physicalTechniques', physicalTechniques]
    ]
    optionalLists.forEach(([key, items]) => {
      if (items.length > 0) {
        tagsToSubmit.push({
          organisation: activeOrg.id,
          key,
          type: 'besmart',
          value: JSON.stringify(items)
        })
      }
    })

    if (tabbedSetupEnabled || clickersToSubmit.length > 0) {
      tagsToSubmit.push({
        organisation: activeOrg.id,
        key: 'quickBehaviours',
        type: 'besmart',
        value: JSON.stringify(clickersToSubmit)
      })
    }

    const params = {
      orgId: activeOrg.id
    }

    await OrganisationTagService.set(apiToken, params, tagsToSubmit)

    activeOrg.wondeSync = wondeIntegration
    activeOrg.enablePILExtras = enablePILExtras;
    activeOrg.aiTermsAccepted = aiTermsAccepted;
    LocalStorageService.set('activeOrg', activeOrg)
    await OrganisationService.updateOrganisation(apiToken, {
      id: activeOrg.id,
      wondeSync: wondeIntegration,
      enablePILExtras: enablePILExtras,
      aiTermsAccepted: aiTermsAccepted,
      cpomsEnabled: cpomsEnabled,
      cpomsSyncEverything: cpomsSyncEverything
    });

    // update the other admins for the organisation, if changed
    if (otherSystemAdminsHasChanged) {

      // get the admins that have been removed
      const removedAdmins = originalOtherSystemAdmins.filter(originalAdmin => !otherSystemAdmins.some(admin => admin.id === originalAdmin.id));
      for (const admin of removedAdmins) {
        await OrganisationService.removeOtherAdmin(apiToken, activeOrg.id, admin.id);
      }

      const newAdmins = otherSystemAdmins.filter(admin => !originalOtherSystemAdmins.some(originalAdmin => originalAdmin.id === admin.id));
      for (const admin of newAdmins) {
        await OrganisationService.addOtherAdmin(apiToken, activeOrg.id, admin.id);
      }
    }

    if (tabbedSetupEnabled && !await saveNotificationRules()) return

    // refresh and set the terms
    const matchedTerms = await TermsService.getCommonTerms()
    LocalStorageService.set('terms', matchedTerms)

    if (tabbedSetupEnabled) {
      const url = new URL(window.location.href)
      url.searchParams.set('tab', activeTabIndex)
      window.history.replaceState(window.history.state, '', url)
    }

    NotificationService.success('Your changes have been saved')
    // force the screen to refresh to ensure the service terms update

    // set a timeout to display a success message
    setTimeout(() => { window.location.reload() }, 1500)
    // window.location.reload()
  }

  const addBehaviourLead = () => {
    // Don't save if all fields empty
    if (serviceDetails.firstName !== '' || serviceDetails.lastName !== '' || serviceDetails.phone !== '' || serviceDetails.email !== '') {
      const lead = {
        firstName: serviceDetails.firstName,
        lastName: serviceDetails.lastName,
        phone: serviceDetails.phone,
        email: serviceDetails.email
      }
      setBehaviourLead(behaviourLeads => [...behaviourLeads, lead])

      setServiceDetails(serviceDetails => ({
        ...serviceDetails,
        firstName: '',
        lastName: '',
        phone: '',
        email: ''
      }))
      // setBehaviourLeadAddButton(false)
    }
  }

  const removeBehaviourLead = (data) => {
    const newLeads = behaviourLeads.filter((item) => item.email !== data.email)

    setBehaviourLead(newLeads)
  }

  const editBehaviourLead = (data) => {
    setServiceDetails(serviceDetails => ({
      ...serviceDetails,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      email: data.email
    }))

    removeBehaviourLead(data)
  }

  const deescalationTechniqueSelect = (technique) => {
    setDeescalationTechnique(
      deescalationTechniques.map(item =>
        item.technique === technique
          ? { ...item, selected: !item.selected }
          : item
      ))
  }

  const addDeescalationTechnique = () => {
    const techniqueExists = deescalationTechniques.find((item) => {
      return (item.technique === serviceDetails.deescalationTechnique)
    })

    if (techniqueExists) {
      NotificationService.error('Deescalation technique already exists, please add a different one')
    } else {
      // Add technique and set to selected
      const technique = {
        technique: serviceDetails.deescalationTechnique,
        selected: true
      }
      setDeescalationTechnique(deescalationTechniques => [...deescalationTechniques, technique])

      // Clear input
      setServiceDetails(serviceDetails => ({
        ...serviceDetails,
        deescalationTechnique: ''
      }))

      // Disable Add button
      setDeescalationAddButton(false)
    }
  }

  const physicalTechniqueSelect = (technique) => {
    setPhysicalTechnique(
      physicalTechniques.map(item =>
        item.technique === technique
          ? { ...item, selected: !item.selected }
          : item
      ))
  }

  const addPhysicalTechnique = () => {
    const techniqueExists = physicalTechniques.find((item) => {
      return (item.technique === serviceDetails.physicalTechnique)
    })

    if (techniqueExists) {
      NotificationService.error('Physical technique already exists, please add a different one')
    } else {
      // Add technique and set to selected
      const technique = {
        technique: serviceDetails.physicalTechnique,
        selected: true
      }
      setPhysicalTechnique(physicalTechniques => [...physicalTechniques, technique])

      // Clear input
      setServiceDetails(serviceDetails => ({
        ...serviceDetails,
        physicalTechnique: ''
      }))

      // Disable Add button
      setPhysicalAddButton(false)
    }
  }


  const updateQuickBehaviour = (rowKey, field, value) => {
    setQuickBehaviours(items => items.map(item => (item.rowKey === rowKey ? { ...item, [field]: value } : item)))
  }

  const addQuickBehaviourRow = () => {
    setQuickBehaviours(items => [...items, { rowKey: nextRowKey(), quickBehaviour: '', selected: true, group: null }])
  }

  const removeQuickBehaviour = (rowKey) => {
    setQuickBehaviours(items => items.filter(item => item.rowKey !== rowKey))
  }

  const addLegacyQuickBehaviour = () => {
    if (quickBehaviours.some(item => item.quickBehaviour === serviceDetails.quickBehaviour)) {
      NotificationService.error('Quick behaviour already exists, please add a different one')
      return
    }
    setQuickBehaviours(items => [...items, { quickBehaviour: serviceDetails.quickBehaviour, selected: true }])
    setServiceDetails(details => ({ ...details, quickBehaviour: '' }))
  }

  const toggleLegacyQuickBehaviour = (name) => {
    setQuickBehaviours(items => items.map(item => item.quickBehaviour === name ? { ...item, selected: !item.selected } : item))
  }

  const removeLegacyQuickBehaviour = (index) => {
    setQuickBehaviours(items => items.filter((item, itemIndex) => itemIndex !== index))
  }

  const goBack = () => {
    void navigate(-1)
  }

  const toggleAcademicYear = (academicYear) => {
    setServiceDetails(serviceDetails => {
      const enabledAcademicYears = Array.isArray(serviceDetails.enabledAcademicYears)
        ? serviceDetails.enabledAcademicYears
        : []
      const isEnabled = enabledAcademicYears.includes(academicYear)

      return {
        ...serviceDetails,
        enabledAcademicYears: isEnabled
          ? enabledAcademicYears.filter(year => year !== academicYear)
          : [...enabledAcademicYears, academicYear]
      }
    })
  }

  // always get the latest settings
  useEffect(() => {
    const refreshOrgData = async () => {
      if (activeOrg?.id) {
        const response = await OrganisationService.getOrganisation(apiToken, activeOrg.id);
        if (response?.data?.length) {
          setWondeIntegration(!!response.data[0]?.wondeSync)
          setEnablePILExtras(!!response.data[0]?.enablePILExtras);
          setAiTermsAccepted(!!response.data[0]?.aiTermsAccepted);

          setCpomsDomain(response.data[0]?.cpomsDomain || null);
          setCpomsEnabled(!!response.data[0]?.cpomsEnabled)
          setCpomsSyncEverything(!!response.data[0]?.cpomsSyncEverything)
        }
      }
    }
    void refreshOrgData()

  }, [activeOrg.id])

  useEffect(() => {
    let unmounted = false

    const getServiceSetup = async () => {
      const params = {
        fields: 'type,key,value',
        limit: 100,
        orgId: activeOrg.id
      }

      const where = {
        type: 'besmart'
      }

      const organisationTags = await OrganisationTagService.get(apiToken, params, where)

      // ToDo: Add error checking and loading spinner
      if (!unmounted) {
        const tags = {}
        let bLeads = []
        let sLeads = []
        let deescalationTechniques = [
          {
            technique: 'Empathy',
            selected: true
          },
          {
            technique: 'Verbal advice and support',
            selected: true
          },
          {
            technique: 'Giving Space',
            selected: true
          },
          {
            technique: 'Reassurance',
            selected: true
          },
          {
            technique: 'Scripts',
            selected: true
          },
          {
            technique: 'Negotiation',
            selected: true
          },
          {
            technique: 'Positive Choices',
            selected: true
          },
          {
            technique: 'Humour',
            selected: true
          },
          {
            technique: 'Logical Consequences',
            selected: true
          },
          {
            technique: 'Tactical Ignoring',
            selected: true
          },
          {
            technique: 'Take up time',
            selected: true
          },
          {
            technique: 'Time-out',
            selected: true
          },
          {
            technique: 'Supportive touch',
            selected: true
          },
          {
            technique: 'Transfer staff',
            selected: true
          },
        ]
        let physicalTechniques = []
        let newQuickBehaviours = []
        let pilTags = {}

        if (organisationTags) {
          organisationTags.forEach(tag => {
            switch (tag.key) {
              case 'serviceUserTerm':
                tags.serviceUserTerm = tag.value
                break
              case 'serviceUserTermPlural':
                tags.serviceUserTermPlural = tag.value
                break
              case 'serviceTerm':
                tags.serviceTerm = tag.value
                break
              case 'serviceTermPlural':
                tags.serviceTermPlural = tag.value
                break
              case 'academicYearStartDay':
                tags.academicYearStartDay = tag.value
                break
              case 'academicYearStartMonth':
                tags.academicYearStartMonth = tag.value
                break
              case 'enabledAcademicYears':
                try {
                  tags.enabledAcademicYears = JSON.parse(tag.value)
                } catch (err) {
                  console.error(err)
                }
                break
              case 'behaviourLeads':
                try {
                  bLeads = JSON.parse(tag.value)
                } catch (err) {
                  console.error(err)
                }
                break
              case 'safeguardingLeads':
                try {
                  sLeads = JSON.parse(tag.value)
                } catch (err) {
                  console.error(err)
                }
                break
              case 'deescalationTechniques':
                try {
                  deescalationTechniques = JSON.parse(tag.value)
                } catch (err) {
                  console.error(err)
                }
                break
              case 'physicalTechniques':
                try {
                  physicalTechniques = JSON.parse(tag.value)
                } catch (err) {
                  console.error(err)
                }
                break

              case 'quickBehaviours':
                try {
                  newQuickBehaviours = JSON.parse(tag.value)
                } catch (err) {
                  console.error(err)
                }
                break
            }
          })

          for (const pilKey of constants.pilKeys) {
            let key = TermsService.getPILTermKeyToTagKey(pilKey.key)
            let matchingTag = organisationTags.find(tag => tag.key === key)
            if (matchingTag !== undefined) {
              pilTags[key] = matchingTag.value
            }
          }
        }

        const recordTermValues = tabbedSetupEnabled
          ? Object.fromEntries(recordTerms.map(({ key }) => [key, organisationTags?.find(tag => tag.key === key)?.value || '']))
          : {}
        setServiceDetails(serviceDetails => ({
          ...serviceDetails,
          serviceUserTerm: tags.serviceUserTerm,
          serviceUserTermPlural: tags.serviceUserTermPlural,
          serviceTerm: tags.serviceTerm,
          serviceTermPlural: tags.serviceTermPlural,
          ...recordTermValues,
          academicYearStartDay: tags.academicYearStartDay,
          academicYearStartMonth: tags.academicYearStartMonth,
          enabledAcademicYears: tags.enabledAcademicYears || [],
          ...pilTags
        }))
        setServiceHasTerms(tags.academicYearStartDay || false)
        setBehaviourLead(bLeads)
        setSafeguardingLeads(sLeads)
        setDeescalationTechnique(deescalationTechniques)
        setPhysicalTechnique(physicalTechniques)
        // Sorted only on load so edits don't move rows; rowKey is client-only
        setQuickBehaviours(tabbedSetupEnabled ? newQuickBehaviours
          .map(item => ({ ...item, group: item.group || null, rowKey: nextRowKey() }))
          .sort(compareClickers) : newQuickBehaviours)
        const loadedColours = {}
        newQuickBehaviours.forEach(item => {
          if (item.group && item.colour && !loadedColours[item.group]) loadedColours[item.group] = item.colour
        })
        setGroupColours(loadedColours)
      }
    }

    setIsLoading(true);
    void getServiceSetup().then(async () => {
      const result = await isAnyAdmin()
      setIsAuthorised(result);
      setIsLoading(false);
    });

    return () => { unmounted = true }
  }, [])

  const fetchOtherAdmins = async () => {
    const otherAdmins = await OrganisationService.getOtherAdmins(apiToken, activeOrg.id);
    const data = otherAdmins?.data || [];
    setOtherSystemAdmins(data);
    setOriginalOtherSystemAdmins(data);
  };
  useEffect(() => {
    void fetchOtherAdmins();
  }, []);

  const getStaff = async () => {

    const params = {
      fields: 'id,firstName,lastName,email,reference,type',
      limit: 1000,
      orgId: activeOrg.id,
      type: 'user'
    };
    const users = await UserService.getUsers(apiToken, params);
    return users;
  };

  useEffect(() => {
    const fetchStaff = async () => {
      const users = await getStaff();
      setAllStaff(users?.data || []);
    };
    void fetchStaff();
  }, []);

  useEffect(() => {
    const fetchStudents = async () => {
      const users = await UserService.getUsers(apiToken, {
        fields: 'id,firstName,lastName,reference,type',
        limit: 2000,
        orgId: activeOrg.id,
        showFormerStudents: 'false',
        type: 'student'
      })
      const students = (users?.data || [])
        .filter(user => user.firstName && user.lastName)
        .map(user => ({ id: user.id, firstName: user.firstName, lastName: user.lastName, name: `${user.firstName} ${user.lastName}` }))
        .sort((a, b) => a.name.localeCompare(b.name))
      setAllStudents(students)
    }
    if (tabbedSetupEnabled) void fetchStudents()
  }, [])

  useEffect(() => {
    const fetchGroups = async () => {
      const groups = await UserGroupService.getGroups(apiToken, { fields: 'id,name', orgId: activeOrg.id })
      setAllStudentGroups((groups?.data || []).map(group => ({ id: group.id, name: group.name })))
    }
    if (tabbedSetupEnabled) void fetchGroups()
  }, [])

  // Behaviour Leads have been altered
  // so rebuild the display list
  const [mappedBehaviourLeads, setMappedBehaviourLeads] = useState([])
  const [behaviourLeadColumns, setBehaviourLeadDataColumns] = useState()

  useEffect(() => {
    setBehaviourLeadDataColumns([
      {
        property: 'firstName',
        header: <Text pad='none' margin='none'>First Name</Text>
      },
      {
        property: 'lastName',
        header: <Text>Last Name</Text>
      },
      {
        property: 'phone',
        header: <Text>Phone</Text>
      },
      {
        property: 'email',
        header: <Text>Email</Text>
      },
      {
        property: 'actions',
        render: datum => (
          <Box direction='row'>
            <ActionButton label={<Text><FontAwesome icon={['fal', 'minus-circle']} /></Text>} margin={{ horizontal: 'xsmall' }} style={{ background: 'besmartOrange' }} onClick={() => removeBehaviourLead(datum)} target='_self' />
            <ActionButton label={<Text><FontAwesome icon={['fal', 'edit']} /></Text>} margin={{ horizontal: 'xsmall' }} style={{ background: 'besmartOrange' }} onClick={() => editBehaviourLead(datum)} target='_self' />
          </Box>
        )
      }
    ])

    const mappedBehaviourLeads = behaviourLeads.map((lead) => ({
      firstName: lead.firstName,
      lastName: lead.lastName,
      phone: lead.phone,
      email: lead.email
    }))

    setMappedBehaviourLeads(mappedBehaviourLeads)
  }, [behaviourLeads])


  // Rule user ids and staff ids can differ in type (string vs number), so compare as strings
  const usedUserIds = notificationRules.filter(rule => rule.user != null).map(rule => String(rule.user))
  const staffOptions = allStaff.map(staff => ({ id: String(staff.id), name: `${staff.firstName} ${staff.lastName}` }))
  // Rules can reference users missing from the staff list; keep them visible instead of blank
  usedUserIds.forEach(id => {
    if (!staffOptions.some(option => option.id === id)) {
      staffOptions.push({ id, name: `Unknown user (${id})` })
    }
  })

  const resultColumns = [
    {
      property: 'user',
      label: 'User',
      size: 'medium',
      verticalAlign: 'top',
      header: <Text pad='none' margin='none' color='secondary' weight='800'>User</Text>,
      render: datum => (
        <Box onClick={event => event.stopPropagation()} onFocus={event => event.stopPropagation()}>
          <RuleSelect
            placeholder='Select user'
            labelKey='name'
            valueKey={{ key: 'id', reduce: true }}
            options={staffOptions.filter(option => option.id === String(datum.user) || !usedUserIds.includes(option.id))}
            value={datum.user == null ? undefined : String(datum.user)}
            onChange={value => {
              const staff = allStaff.find(item => String(item.id) === value)
              updateRule(datum.rowKey, 'user', staff ? staff.id : null)
            }}
          />
        </Box>
      )
    },
    {
      property: 'yearGroup',
      label: 'Year Groups',
      verticalAlign: 'top',
      header: <Text pad='none' margin='none' color='secondary' weight='800'>Year Group</Text>,
      render: datum => (
        <Box onClick={event => event.stopPropagation()} onFocus={event => event.stopPropagation()}>
          <RuleSelect
            multiple
            placeholder='Select year groups'
            options={allAcademicYears}
            value={datum.yearGroup}
            onChange={value => updateRule(datum.rowKey, 'yearGroup', value || [])}
          />
        </Box>
      )
    },
    {
      property: 'studentGroup',
      label: 'Groups',
      verticalAlign: 'top',
      header: <Text pad='none' margin='none' color='secondary' weight='800'>Groups</Text>,
      render: datum => (
        <Box onClick={event => event.stopPropagation()} onFocus={event => event.stopPropagation()}>
          <RuleSelect
            multiple
            placeholder='Select groups'
            labelKey='name'
            valueKey={{ key: 'id', reduce: true }}
            options={allStudentGroups}
            savedOptions={datum.studentGroup}
            value={datum.studentGroup.map(group => group.id)}
            onChange={ids => updateRule(
              datum.rowKey,
              'studentGroup',
              (ids || []).map(id => allStudentGroups.find(group => group.id === id) || datum.studentGroup.find(group => group.id === id))
            )}
          />
        </Box>
      )
    },
    {
      property: 'student',
      label: serviceDetails.serviceUserTermPlural || 'Students',
      verticalAlign: 'top',
      header: <Text pad='none' margin='none' color='secondary' weight='800'>{serviceDetails.serviceUserTermPlural || 'Students'}</Text>,
      render: datum => (
        <Box onClick={event => event.stopPropagation()} onFocus={event => event.stopPropagation()}>
          <RuleSelect
            multiple
            placeholder='Select students'
            labelKey='name'
            valueKey={{ key: 'id', reduce: true }}
            options={allStudents}
            savedOptions={datum.student.map(student => ({ ...student, name: `${student.firstName} ${student.lastName}` }))}
            value={datum.student.map(student => student.id)}
            onChange={ids => updateRule(
              datum.rowKey,
              'student',
              (ids || []).map(id => {
                const found = allStudents.find(student => student.id === id) || datum.student.find(student => student.id === id)
                return { id: found.id, firstName: found.firstName, lastName: found.lastName }
              })
            )}
          />
        </Box>
      )
    },
    {
      property: 'actions',
      label: 'Actions',
      header: <Text pad='none' margin='none' color='secondary' weight='800'>Actions</Text>,
      render: datum => (
        <ActionButton label={<Text><FontAwesome icon={['fal', 'minus-circle']} /></Text>} margin={{ horizontal: 'xsmall' }} style={{ background: 'besmartOrange' }} onClick={() => removeRuleRow(datum.rowKey)} target='_self' />
      )
    }
  ]

  const clickerGroups = [...new Set(quickBehaviours.map(item => item.group).filter(Boolean))].sort(compareText)

  const clickerColumns = [
    {
      property: 'quickBehaviour',
      header: <Text pad='none' margin='none' color='secondary' weight='800'>Clicker</Text>,
      // No clickable Box wrapper: Grommet's Keyboard handling would swallow typed spaces
      render: datum => (
        <TextInput
          aria-label='Clicker name'
          placeholder='Clicker name'
          value={datum.quickBehaviour}
          onChange={event => updateQuickBehaviour(datum.rowKey, 'quickBehaviour', event.target.value)}
        />
      )
    },
    {
      property: 'group',
      header: <Text pad='none' margin='none' color='secondary' weight='800'>Group</Text>,
      render: datum => (
        <RuleSelect
          creatable
          placeholder='Select or add group'
          options={clickerGroups}
          value={datum.group}
          onChange={value => updateQuickBehaviour(datum.rowKey, 'group', (value || '').trim() || null)}
        />
      )
    },
    {
      property: 'selected',
      header: <Text pad='none' margin='none' color='secondary' weight='800'>Enabled</Text>,
      render: datum => (
        <CheckBox
          a11yTitle={`Enable ${datum.quickBehaviour || 'clicker'}`}
          checked={!!datum.selected}
          onChange={() => updateQuickBehaviour(datum.rowKey, 'selected', !datum.selected)}
        />
      )
    },
    {
      property: 'actions',
      header: <Text pad='none' margin='none' color='secondary' weight='800'>Actions</Text>,
      render: datum => (
        <ActionButton label={<Text><FontAwesome icon={['fal', 'minus-circle']} /></Text>} margin={{ horizontal: 'xsmall' }} style={{ background: 'besmartOrange' }} onClick={() => removeQuickBehaviour(datum.rowKey)} target='_self' />
      )
    }
  ]

  const colourOptionLabel = option => (
    <Box direction='row' align='center' gap='small'>
      <Box background={option.value} pad='xsmall' round='xxsmall' flex={false} />
      <Text>{option.label}</Text>
    </Box>
  )

  const clickerGroupColumns = [
    {
      property: 'group',
      header: <Text pad='none' margin='none' color='secondary' weight='800'>Group</Text>,
      render: datum => <Text>{datum.group}</Text>
    },
    {
      property: 'colour',
      header: <Text pad='none' margin='none' color='secondary' weight='800'>Colour</Text>,
      render: datum => (
        <RuleSelect
          placeholder={`Select colour for ${datum.group}`}
          labelKey='label'
          valueKey={{ key: 'value', reduce: true }}
          options={constants.clickerColours}
          value={groupColours[datum.group] || null}
          formatOptionLabel={colourOptionLabel}
          onChange={value => setGroupColours(colours => ({ ...colours, [datum.group]: value || null }))}
        />
      )
    }
  ]

  const sections = {
    details: <ServiceDetailsContent tabbedSetupEnabled={tabbedSetupEnabled} serviceHasTerms={serviceHasTerms} setServiceHasTerms={setServiceHasTerms} allAcademicYears={allAcademicYears} serviceDetails={serviceDetails} toggleAcademicYear={toggleAcademicYear} />,
    techniques: <TechniquesContent deescalationTechniques={deescalationTechniques} deescalationTechniqueSelect={deescalationTechniqueSelect} removeTechnique={removeTechnique} deescalationAddButton={deescalationAddButton} addDeescalationTechnique={addDeescalationTechnique} physicalTechniques={physicalTechniques} physicalTechniqueSelect={physicalTechniqueSelect} physicalAddButton={physicalAddButton} addPhysicalTechnique={addPhysicalTechnique} />,
    clickers: <ClickersContent tabbedSetupEnabled={tabbedSetupEnabled} clickerColumns={clickerColumns} quickBehaviours={quickBehaviours} addQuickBehaviourRow={addQuickBehaviourRow} clickerGroups={clickerGroups} clickerGroupColumns={clickerGroupColumns} toggleLegacyQuickBehaviour={toggleLegacyQuickBehaviour} removeLegacyQuickBehaviour={removeLegacyQuickBehaviour} serviceDetails={serviceDetails} addLegacyQuickBehaviour={addLegacyQuickBehaviour} />,
    permissions: <PermissionsTabContent isLoading={isLoading} setSafeguardingLeads={setSafeguardingLeads} allStaff={allStaff} safeguardingLeads={safeguardingLeads} setOtherSystemAdmins={setOtherSystemAdmins} setOtherSystemAdminsHasChanged={setOtherSystemAdminsHasChanged} otherSystemAdmins={otherSystemAdmins} />,
    recipients: <RecipientsContent tabbedSetupEnabled={tabbedSetupEnabled} behaviourLeadColumns={behaviourLeadColumns} mappedBehaviourLeads={mappedBehaviourLeads} addBehaviourLead={addBehaviourLead} />,
    rules: tabbedSetupEnabled ? <NotificationRulesContent resultColumns={resultColumns} notificationRules={notificationRules} addRuleRow={addRuleRow} /> : null,
    pil: <PILTabContent enablePILExtras={enablePILExtras} setEnablePILExtras={setEnablePILExtras} />,
    ai: <AIContent tabbedSetupEnabled={tabbedSetupEnabled} aiTermsAccepted={aiTermsAccepted} setAiTermsAccepted={setAiTermsAccepted} />,
    wonde: <WondeContent wondeIntegration={wondeIntegration} setWondeIntegration={setWondeIntegration} tabbedSetupEnabled={tabbedSetupEnabled} />,
    cpoms: <CPOMSContent cpomsDomain={cpomsDomain} cpomsEnabled={cpomsEnabled} setCpomsEnabled={setCpomsEnabled} cpomsSyncEverything={cpomsSyncEverything} setCpomsSyncEverything={setCpomsSyncEverything} tabbedSetupEnabled={tabbedSetupEnabled} />
  }

  return (
    <Box margin={{ bottom: 'xlarge' }}>
      <BreadcrumbBar
        title="Service Setup"
        path={
          <>
            <Anchor href="/">BehaviourSmart</Anchor>
            <Text color="besmartBlue" size="xsmall">
              {window.location.pathname}
            </Text>
          </>
        }
      />
      {isLoading && <Box width='xlarge' gap='medium' margin={{ bottom: 'medium' }} pad={{ vertical: 'medium' }} align='center' justify='center' background='white' round='small'>
        <Text size='large' weight='bold' color='dark-3'>Loading...</Text>
        <PulseLoader
          size={15}
          color='#ff8300'
        />
      </Box>}

      {!isLoading && !isAuthorised && <Box width='xlarge' gap='small' margin={{ bottom: 'medium' }} pad={{ vertical: 'medium' }} align='center' justify='center' background='white' round='small'>
        <Text size='large' weight='bold' color='secondary'>You are not authorised to view this page</Text>
        <Text size='medium' color='secondary'>Please contact your system administrator if you believe this is an error</Text>
      </Box>}

      {!isLoading && isAuthorised && <>
        {!tabbedSetupEnabled && <>
          {sections.wonde}
          {sections.cpoms}
          {!serviceDetails.serviceTerm && <Box background="white" pad="small" margin={{ bottom: 'medium' }}>
            <Text margin={{ top: 'xsmall' }} size="large">Setup to Continue</Text>
            <Text margin={{ bottom: 'xsmall' }}>Please complete the following information before using BehaviourSmart.</Text>
          </Box>}
        </>}
        <Box background='white' direction='column' gap='small' round='small' flex='grow'>
        <Box
          gap='small'
          margin={{ horizontal: 'small' }}
          pad='small'
          round='medium'
        >
          <Box direction='column' gap='medium'>
            <Form
              onChange={nextValue => {
                // Enable Behaviour Lead Add button if all fields have data
                // setBehaviourLeadAddButton(nextValue.firstName !== '' && nextValue.lastName !== '' && nextValue.phone !== '' && nextValue.email !== '')

                // Enable Deescalation Technique Add button if field has data
                setDeescalationAddButton(nextValue.deescalationTechnique);
                // Enable Physical Technique Add button if field has data
                setPhysicalAddButton(nextValue.physicalTechnique);
                setServiceDetails(nextValue);
              }}
              onSubmit={({ value: nextValue }) => {
                void submitForm(nextValue);
              }}
              value={serviceDetails}
            >
              {tabbedSetupEnabled ? <ServiceSetupTabs sections={sections} activeIndex={activeTabIndex} onActive={setActiveTabIndex} /> : <>
                {sections.details}
                {sections.recipients}
                {sections.permissions}
                {sections.techniques}
                {sections.clickers}
                {sections.ai}
                {sections.pil}
              </>}

              <Divider
                color="primary"
                margin={{ top: 'medium', bottom: 'none' }}
              />

              <Box
                direction="row"
                justify="between"
                margin={{ vertical: 'medium' }}
              >
                <ActionButton label="< Back" onClick={() => goBack()} secondary />
                <ActionButton type="submit" label="Save" primary />
              </Box>
            </Form>
          </Box>
        </Box>
      </Box></>}
    </Box>
  );
}

export default ServiceSetup
